import { MIN_TRANSFER_SECONDS, type Footpath, type Network } from './network';
import type { RealtimeOverlay } from './realtime';

/**
 * `1` searches forward from a departure time (« Partir à »), `-1` backward from
 * an arrival time (« Arriver à »): the same scan with the clock reversed.
 */
export type Direction = 1 | -1;

/** A stop the search starts from or ends at, with the walk between it and the passenger's place. */
export interface Access {
  stop: number;
  seconds: number;
  distance: number;
}

export interface RaptorQuery {
  direction: Direction;
  /** Departure time forward, latest arrival time backward, in epoch seconds. */
  time: number;
  /** Where the scan starts: the origin forward, the destination backward. */
  sources: Access[];
  targets: Access[];
  /** Trips per journey: 4 allows 3 transfers. */
  maxRounds: number;
  timetable: Timetable;
}

/** Steps in scan order: from the sources to the targets, which is time order only forward. */
export type RaptorLeg =
  | { kind: 'transit'; pattern: number; trip: number; from: number; to: number }
  | { kind: 'walk'; from: number; to: number; footpath: Footpath };

export interface RaptorJourney {
  /** Trips taken. */
  rounds: number;
  /** Time the passenger reaches the target place: arrival forward, departure backward. */
  time: number;
  source: Access;
  target: Access;
  legs: RaptorLeg[];
}

interface TripTimes {
  arrivals: number[];
  departures: number[];
  skipped?: boolean[];
  canceled: boolean;
}

/** Times of each pattern's trips for one search, live data applied. */
export interface Timetable {
  trips: TripTimes[][];
  /** No trip of the pattern overtakes another: the earliest one can be found by bisection. */
  fifo: boolean[];
}

const staticTimetables = new WeakMap<Network, Timetable>();

function isFifo(trips: TripTimes[]): boolean {
  for (let i = 1; i < trips.length; i++) {
    const [previous, trip] = [trips[i - 1], trips[i]];
    for (let position = 0; position < trip.departures.length; position++) {
      if (trip.departures[position] < previous.departures[position]) return false;
      if (trip.arrivals[position] < previous.arrivals[position]) return false;
    }
  }
  return true;
}

/** The scheduled timetable is kept per network; only the patterns with live data are redone. */
export function buildTimetable(network: Network, overlay?: RealtimeOverlay): Timetable {
  let scheduled = staticTimetables.get(network);
  if (!scheduled) {
    const trips = network.patterns.map(pattern => pattern.trips.map(trip => ({ ...trip, canceled: false })));
    scheduled = { trips, fifo: trips.map(isFifo) };
    staticTimetables.set(network, scheduled);
  }
  if (!overlay || overlay.size === 0) return scheduled;

  const trips = [...scheduled.trips];
  const fifo = [...scheduled.fifo];
  const touched = new Set<number>();
  overlay.forEach((times, key) => {
    const located = network.tripsByKey.get(key);
    if (!located) return;
    if (!touched.has(located.pattern)) {
      touched.add(located.pattern);
      trips[located.pattern] = [...trips[located.pattern]];
    }
    trips[located.pattern][located.index] = times;
  });
  touched.forEach(pattern => (fifo[pattern] = isFifo(trips[pattern])));
  return { trips, fifo };
}

type TripParent = { pattern: number; trip: number; from: number; to: number };
type FootParent =
  | { kind: 'source'; access: Access }
  | { kind: 'stay'; round: number }
  | { kind: 'walk'; round: number; from: number; footpath: Footpath };

/**
 * RAPTOR (Delling, Pajor, Werneck, 2012): round k holds the best times reachable with
 * k trips. Returns the Pareto set on (time, trips): one journey per round that beats
 * every journey with fewer trips.
 */
export function raptor(network: Network, query: RaptorQuery): RaptorJourney[] {
  const { direction: d, time, sources, targets, maxRounds, timetable } = query;
  const stopCount = network.stopIds.length;
  const isBetter = (a: number, b: number) => d * a < d * b;
  const unreached = d * Infinity;

  const targetAccess = new Map<number, Access>();
  targets.forEach(target => {
    const known = targetAccess.get(target.stop);
    if (!known || target.seconds < known.seconds) targetAccess.set(target.stop, target);
  });

  // `onFoot[k]`: at the stop and free to board, with k trips taken. `byTrip[k]`: just off the k-th trip.
  const onFoot: Float64Array[] = [new Float64Array(stopCount).fill(unreached)];
  const onFootParent: Array<Array<FootParent | undefined>> = [new Array(stopCount)];
  const byTrip: Float64Array[] = [new Float64Array(stopCount).fill(unreached)];
  const byTripParent: Array<Array<TripParent | undefined>> = [new Array(stopCount)];
  const bestByTrip = new Float64Array(stopCount).fill(unreached);
  let bestTargetTime = unreached;

  let marked = new Set<number>();
  sources.forEach(access => {
    const reached = time + d * access.seconds;
    if (isBetter(reached, onFoot[0][access.stop])) {
      onFoot[0][access.stop] = reached;
      onFootParent[0][access.stop] = { kind: 'source', access };
      marked.add(access.stop);
    }
  });

  const journeys: RaptorJourney[] = [];

  for (let k = 1; k <= maxRounds && marked.size > 0; k++) {
    onFoot[k] = Float64Array.from(onFoot[k - 1]);
    onFootParent[k] = [...onFootParent[k - 1]];
    byTrip[k] = new Float64Array(stopCount).fill(unreached);
    byTripParent[k] = new Array(stopCount);

    // Each pattern is scanned from the first marked stop it reaches, in scan order.
    const queue = new Map<number, number>();
    marked.forEach(stop =>
      network.stopPatterns[stop].forEach(({ pattern, position }) => {
        const queued = queue.get(pattern);
        if (queued === undefined || d * position < d * queued) queue.set(pattern, position);
      }),
    );

    const improved = new Set<number>();
    queue.forEach((startPosition, patternIndex) => {
      const pattern = network.patterns[patternIndex];
      const times = timetable.trips[patternIndex];
      const fifo = timetable.fifo[patternIndex];
      const boardOK = d === 1 ? pattern.canBoard : pattern.canAlight;
      const alightOK = d === 1 ? pattern.canAlight : pattern.canBoard;
      const boardTime = (trip: TripTimes, position: number) =>
        d === 1 ? trip.departures[position] : trip.arrivals[position];
      const reachTime = (trip: TripTimes, position: number) =>
        d === 1 ? trip.arrivals[position] : trip.departures[position];

      let current = -1;
      let boardedAt = -1;
      for (let position = startPosition; position >= 0 && position < pattern.stops.length; position += d) {
        const stop = pattern.stops[position];

        if (current >= 0 && alightOK[position] && !times[current].skipped?.[position]) {
          const reached = reachTime(times[current], position);
          if (isBetter(reached, bestByTrip[stop]) && isBetter(reached, bestTargetTime)) {
            byTrip[k][stop] = reached;
            bestByTrip[stop] = reached;
            byTripParent[k][stop] = { pattern: patternIndex, trip: current, from: boardedAt, to: position };
            improved.add(stop);
          }
        }

        const label = onFoot[k - 1][stop];
        if (label === unreached || !boardOK[position]) continue;
        if (current >= 0 && isBetter(boardTime(times[current], position), label)) continue;
        const catchable = firstCatchableTrip(times, fifo, position, label, d, boardTime);
        if (
          catchable >= 0 &&
          (current < 0 ||
            isBetter(boardTime(times[catchable], position), boardTime(times[current], position)))
        ) {
          current = catchable;
          boardedAt = position;
        }
      }
    });

    let roundBest: { time: number; stop: number; access: Access } | undefined;
    improved.forEach(stop => {
      const access = targetAccess.get(stop);
      if (!access) return;
      const reached = byTrip[k][stop] + d * access.seconds;
      if (!roundBest || isBetter(reached, roundBest.time)) roundBest = { time: reached, stop, access };
    });
    if (roundBest && isBetter(roundBest.time, bestTargetTime)) {
      bestTargetTime = roundBest.time;
      journeys.push(rebuild(k, roundBest.stop, roundBest.time, roundBest.access));
    }

    marked = new Set();
    improved.forEach(stop => {
      relax(k, stop, stop, { kind: 'stay', round: k }, MIN_TRANSFER_SECONDS);
      network.footpaths[stop].forEach(footpath =>
        relax(k, stop, footpath.to, { kind: 'walk', round: k, from: stop, footpath }, footpath.walkSeconds),
      );
    });
  }

  function relax(k: number, from: number, to: number, parent: FootParent, walk: number) {
    const reached = byTrip[k][from] + d * Math.max(walk, MIN_TRANSFER_SECONDS);
    if (isBetter(reached, onFoot[k][to]) && isBetter(reached, bestTargetTime)) {
      onFoot[k][to] = reached;
      onFootParent[k][to] = parent;
      marked.add(to);
    }
  }

  function rebuild(round: number, targetStop: number, reached: number, target: Access): RaptorJourney {
    const legs: RaptorLeg[] = [];
    let stop = targetStop;
    let k = round;
    let source: Access | undefined;
    while (!source) {
      const trip = byTripParent[k][stop]!;
      legs.push({ kind: 'transit', ...trip });
      const boardStop = network.patterns[trip.pattern].stops[trip.from];
      const parent = onFootParent[k - 1][boardStop]!;
      if (parent.kind === 'source') {
        source = parent.access;
      } else if (parent.kind === 'stay') {
        stop = boardStop;
        k = parent.round;
      } else {
        legs.push({ kind: 'walk', from: parent.from, to: boardStop, footpath: parent.footpath });
        stop = parent.from;
        k = parent.round;
      }
    }
    return { rounds: round, time: reached, source, target, legs: legs.reverse() };
  }

  return journeys;
}

/** The trip of the pattern leaving the stop first once the passenger is there (arriving last, backward). */
function firstCatchableTrip(
  trips: TripTimes[],
  fifo: boolean,
  position: number,
  label: number,
  d: Direction,
  boardTime: (trip: TripTimes, position: number) => number,
): number {
  const catchable = (i: number) => d * boardTime(trips[i], position) >= d * label;
  const usable = (i: number) => !trips[i].canceled && !trips[i].skipped?.[position];

  if (fifo) {
    // Forward, the first catchable trip in departure order; backward, the last one.
    let low = 0;
    let high = trips.length;
    while (low < high) {
      const middle = (low + high) >> 1;
      const beforeBoundary = d === 1 ? !catchable(middle) : catchable(middle);
      if (beforeBoundary) low = middle + 1;
      else high = middle;
    }
    for (let i = d === 1 ? low : low - 1; i >= 0 && i < trips.length; i += d) {
      if (usable(i)) return i;
    }
    return -1;
  }

  let best = -1;
  for (let i = 0; i < trips.length; i++) {
    if (!usable(i) || !catchable(i)) continue;
    if (best < 0 || d * boardTime(trips[i], position) < d * boardTime(trips[best], position)) best = i;
  }
  return best;
}
